import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';

interface GateTextProps {
    children: React.ReactNode;
    className?: string;
}

/**
 * The heading and its supporting line, shared by every step of every gate.
 *
 * They live together because the pair is what has to stay consistent — the gap
 * between them reads as one rhythm across the auth modal, the enquiry gate and
 * the passkey steps, and it drifted when each step owned its own spacing.
 *
 * Both sit flush with the fields and buttons beneath them. An optical inset was
 * tried against the pill-shaped fields and read as a misaligned left edge
 * instead, because everything else in the column — labels, inputs, the primary
 * button — starts at the body's own padding.
 *
 * The mobile treatment cannot be a `md:` variant. The sheet is chosen by the
 * UA display mode, so a phone held in landscape gets it while measuring wider
 * than `md` — a breakpoint rule would style that phone as a desktop.
 */
export const GateHeading = ({ children, className }: GateTextProps) => {
    return (
        <h2
            className={cn(
                'text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]',
                className,
            )}
        >
            {children}
        </h2>
    );
};

export const GateSubcopy = ({ children, className }: GateTextProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    return (
        <p
            className={cn(
                'mt-4 tracking-[-0.02em] text-[#828282]',
                isMobile ? 'text-base' : 'text-[1.1875rem]',
                className,
            )}
        >
            {children}
        </p>
    );
};
